/**
 * ReferralTile — the Dashboard's prominent referral card.
 *
 *   "Refer another store — you both get bonus credits"
 *   + one-tap copy of the share link, the in-flight referral's stage
 *   ("Day 2 of trial" / "Waiting for their first paid charge" /
 *   "🎉 Rewarded — 6 image ads, 1 product build… added") and the bonus
 *   credits the merchant currently holds with days until they expire.
 *
 * Data: GET /api/referral/status (lib/referral.js, cached 30s); refetches
 * on the REFERRAL_CHANGED_EVENT so a share / dismissal elsewhere updates it.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Text } from '@shopify/polaris';
import {
  fetchReferralStatus, runShare, stageTone, REFERRALS_ROUTE, REFERRAL_CHANGED_EVENT,
} from '../lib/referral';

export default function ReferralTile({ shop }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!shop) return undefined;
    let alive = true;
    const load = (force) => fetchReferralStatus({ force }).then((s) => { if (alive) setStatus(s); }).catch(() => {});
    load(false);
    const onChange = () => load(true);
    window.addEventListener(REFERRAL_CHANGED_EVENT, onChange);
    return () => { alive = false; window.removeEventListener(REFERRAL_CHANGED_EVENT, onChange); };
  }, [shop]);

  const copyLink = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const { copied: ok } = await runShare('copy');
      setCopied(ok);
      if (ok) setTimeout(() => setCopied(false), 2500);
    } catch { /* the referrals page has every channel */ }
    setBusy(false);
  };

  const program = status?.program;
  const active = status?.activeReferral || null;
  const latest = status?.referrals?.find((r) => !r.legacy) || null;
  const rewarded = !active && latest?.status === 'REWARDED' ? latest : null;
  const bonuses = status?.bonuses || [];
  const soonest = bonuses.reduce((m, b) => (b.daysLeft != null && (m == null || b.daysLeft < m) ? b.daysLeft : m), null);

  return (
    <div
      data-testid="referral-tile"
      role="region"
      aria-label="Referral program"
      style={{
        position: 'relative', overflow: 'hidden', borderRadius: 16, padding: '20px 22px',
        background: 'linear-gradient(135deg, #1a1333 0%, #2b1e5e 55%, #1c2f66 100%)',
        color: 'white', boxShadow: '0 14px 40px rgba(43, 30, 94, 0.28)',
        display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0, 1.6fr) minmax(240px, 1fr)', alignItems: 'center',
      }}
    >
      <div aria-hidden="true" style={{ position: 'absolute', right: -40, top: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(107,78,232,0.55) 0%, rgba(107,78,232,0) 70%)' }} />
      <div style={{ position: 'relative', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)' }}>
            Referral program
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: 'rgba(255,255,255,0.14)' }}>
            Both get bonus credits
          </span>
        </div>
        <h2 style={{ margin: '8px 0 6px', fontSize: 21, lineHeight: 1.2, fontWeight: 800 }}>
          Refer another store — you both get bonus credits
        </h2>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'rgba(255,255,255,0.82)' }}>
          {program
            ? <>They start with <strong>{program.referredBonusLabel}</strong>. You get <strong>{program.referrerBonusLabel}</strong> when their first paid month starts — used before your plan credits, never touching your bill.</>
            : 'They start with a welcome credit pack; you get a bigger one when their first paid month starts. Never touches your bill.'}
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={copyLink}
            disabled={!status || busy}
            data-testid="referral-tile-copy"
            style={{
              border: 'none', borderRadius: 10, padding: '10px 16px', cursor: status ? 'pointer' : 'default',
              fontWeight: 800, fontSize: 14, color: '#17133a', background: 'white', opacity: status ? 1 : 0.7,
            }}
          >
            {copied ? '✓ Link copied' : '🔗 Copy my link'}
          </button>
          <button
            type="button"
            onClick={() => navigate(REFERRALS_ROUTE)}
            data-testid="referral-tile-share"
            style={{
              border: '1.5px solid rgba(255,255,255,0.55)', background: 'transparent', color: 'white',
              borderRadius: 10, padding: '10px 16px', cursor: 'pointer', fontWeight: 700, fontSize: 14,
            }}
          >
            Share & track →
          </button>
        </div>
      </div>

      <div
        style={{
          position: 'relative', background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.16)',
          borderRadius: 14, padding: '14px 16px', display: 'grid', gap: 8, minWidth: 0,
        }}
        data-testid="referral-tile-status"
      >
        {!status ? (
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>Loading your referral status…</span>
        ) : active ? (
          <>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)' }}>Your referral</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>{active.shop}</span>
              <Badge tone={stageTone(active.stage?.key)}>{active.stage?.label || active.status}</Badge>
            </div>
          </>
        ) : rewarded ? (
          <>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)' }}>Your referral</span>
            <span style={{ fontWeight: 700, fontSize: 14 }}>🎉 Rewarded — {status.bonusSummary ? `${status.bonusSummary} added` : 'bonus credits added'}</span>
          </>
        ) : (
          <>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)' }}>No referral yet</span>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.45 }}>
              Know another store owner? Send your link — you&rsquo;ll see their progress here the moment they install.
            </span>
          </>
        )}
        {bonuses.length > 0 && (
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.16)', paddingTop: 8, marginTop: 2 }}>
            <Text as="p" variant="bodySm">
              <span style={{ color: 'rgba(255,255,255,0.9)' }}>
                You hold <strong>{status.bonusSummary}</strong>
                {soonest != null ? ` — expiring in ${soonest} day${soonest === 1 ? '' : 's'}` : ''}. Used before your plan credits.
              </span>
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}
